import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, User, ChevronRight, BookOpen } from "lucide-react";
import { useLanguage } from "../i18n/LanguageProvider";
import Seo from "../seo/Seo";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatDate = (dateStr, lang) => {
  const date = new Date(dateStr);
  const locales = { nl: "nl-NL", en: "en-GB", fr: "fr-FR", de: "de-DE" };
  return date.toLocaleDateString(locales[lang] || "nl-NL", {
    year: "numeric", month: "long", day: "numeric"
  });
};

const ArticleCard = ({ article, language, t, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
  >
    <Link
      to={`/blog/${article.slug}`}
      className="group block rounded-xl overflow-hidden bg-[#121212] border border-white/10 hover:border-[#4DD0E1]/40 transition-all duration-300"
      data-testid={`blog-card-${article.slug}`}
    >
      <div className="aspect-[16/9] overflow-hidden">
        <img
          src={article.image}
          alt={article.title[language] || article.title.nl}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="px-2.5 py-1 rounded-full bg-[#4DD0E1]/10 text-[#4DD0E1] text-xs font-medium">
            {t.blog.categories[article.category] || article.category}
          </span>
          <span className="flex items-center gap-1 text-xs text-[#A1A1AA]">
            <Clock size={12} />
            {formatDate(article.created_at, language)}
          </span>
        </div>
        <h2 className="text-lg font-bold text-white group-hover:text-[#4DD0E1] transition-colors mb-2 line-clamp-2">
          {article.title[language] || article.title.nl}
        </h2>
        <p className="text-sm text-[#A1A1AA] line-clamp-3 mb-4">
          {article.excerpt[language] || article.excerpt.nl}
        </p>
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4DD0E1] group-hover:gap-2.5 transition-all">
          {t.blog.readMore} <ChevronRight size={16} />
        </span>
      </div>
    </Link>
  </motion.div>
);

export default function BlogPage() {
  const { language, t } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    axios.get(`${API}/blog?lang=${language}`)
      .then(res => setArticles(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [language]);

  const categories = [...new Set(articles.map(a => a.category))];
  const filtered = filter === "all" ? articles : articles.filter(a => a.category === filter);

  const blogTitles = {
    nl: "Blog — Kennis & Tips | Clean-Force BV",
    en: "Blog — Knowledge & Tips | Clean-Force BV",
    fr: "Blog — Connaissances & Conseils | Clean-Force BV",
    de: "Blog — Wissen & Tipps | Clean-Force BV",
  };
  const blogDescs = {
    nl: "Praktische artikelen over professionele schoonmaak, onderhoud en hygiëne van Clean-Force.",
    en: "Practical articles about professional cleaning, maintenance and hygiene from Clean-Force.",
    fr: "Articles pratiques sur le nettoyage professionnel, l'entretien et l'hygiène par Clean-Force.",
    de: "Praktische Artikel über professionelle Reinigung, Wartung und Hygiene von Clean-Force.",
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="blog-page">
      <Seo
        path="/blog"
        lang={language}
        title={blogTitles[language] || blogTitles.nl}
        description={blogDescs[language] || blogDescs.nl}
      />
      {/* Header */}
      <div className="bg-[#121212] border-b border-white/10 pt-20">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#4DD0E1] transition-colors mb-6"
            data-testid="blog-back-home"
          >
            <ArrowLeft size={16} /> {t.blog.backToHome}
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <BookOpen size={20} className="text-[#4DD0E1]" />
            <span className="text-[#4DD0E1] text-xs tracking-[0.2em] uppercase font-bold">
              {t.blog.badge}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white" data-testid="blog-title">
            {t.blog.title}
          </h1>
          <p className="text-[#A1A1AA] mt-2 max-w-xl">{t.blog.subtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Category filter */}
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-filters">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === "all"
                  ? "bg-[#4DD0E1] text-black"
                  : "bg-white/5 text-[#A1A1AA] hover:text-white hover:bg-white/10"
              }`}
              data-testid="blog-filter-all"
            >
              Alle
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  filter === cat
                    ? "bg-[#4DD0E1] text-black"
                    : "bg-white/5 text-[#A1A1AA] hover:text-white hover:bg-white/10"
                }`}
                data-testid={`blog-filter-${cat}`}
              >
                {t.blog.categories[cat] || cat}
              </button>
            ))}
          </div>
        )}

        {/* Articles grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#4DD0E1] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#A1A1AA]" data-testid="blog-no-articles">
            <BookOpen size={48} className="mx-auto mb-4 opacity-40" />
            <p>{t.blog.noArticles}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
            {filtered.map((article, idx) => (
              <ArticleCard
                key={article.slug}
                article={article}
                language={language}
                t={t}
                index={idx}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
